/**
 * CR335: Shared hook for resolving Supabase storage URLs (public or signed).
 * Extracted from FlyerEvidenceSection for reuse across flyer-related components.
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/** Resolve a storage path to bucket + object path */
function resolveBucketPath(rawPath: string): { bucket: string; path: string } | null {
  if (!rawPath) return null;
  const knownBuckets = ['flyer-snippets', 'flyer-poc-assets', 'flyer-fetch-assets', 'flyer-pdfs'];
  for (const bucket of knownBuckets) {
    if (rawPath.startsWith(`${bucket}/`)) {
      return { bucket, path: rawPath.slice(bucket.length + 1) };
    }
  }
  // "rendered/..." paths are in flyer-fetch-assets (not flyer-poc-assets)
  if (rawPath.startsWith('rendered/')) {
    return { bucket: 'flyer-fetch-assets', path: rawPath };
  }
  // CR445: Bare snippet paths (no bucket prefix) default to flyer-snippets
  return { bucket: 'flyer-snippets', path: rawPath };
}

/**
 * Hook that resolves a storage path to a usable URL.
 * Uses public URL for flyer-snippets bucket, signed URLs for private buckets.
 */
export function useSignedStorageUrl(pageImagePath: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pageImagePath) { setUrl(null); return; }

    const resolved = resolveBucketPath(pageImagePath);
    if (!resolved) { setUrl(null); return; }

    // CR447: Always use signed URLs for all buckets (public URL path was unreliable)
    let cancelled = false;
    setLoading(true);
    supabase.storage
      .from(resolved.bucket)
      .createSignedUrl(resolved.path, 3600) // 1 hour
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data?.signedUrl) {
          setUrl(data.signedUrl);
        } else {
          console.warn('[useSignedStorageUrl] Failed to get signed URL:', resolved.bucket, resolved.path, error?.message);
          setUrl(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [pageImagePath]);

  return { url, loading };
}
